import { Injectable } from '@angular/core';
import * as fabric from 'fabric';

interface TouchData {
  identifier: number;
  clientX: number;
  clientY: number;
  target: EventTarget | null;
}

@Injectable({
  providedIn: 'root'
})
export class MobileGestureService {
  private touches: Map<number, TouchData> = new Map();
  private lastPinchDistance = 0;
  private isPinching = false;
  private isLongPress = false;
  private longPressTimeout?: ReturnType<typeof setTimeout>;


  setupMobileGestures(canvas: fabric.Canvas): void {
    const canvasElement = canvas.getElement();

    // Disable default touch behaviors
    canvasElement.style.touchAction = 'none';
    canvasElement.style.userSelect = 'none';

    // Ensure mouse interactions still work
    canvasElement.style.cursor = 'default';

    // Touch event listeners
    canvasElement.addEventListener('touchstart', (e) => this.handleTouchStart(e, canvas), { passive: false });
    canvasElement.addEventListener('touchmove', (e) => this.handleTouchMove(e, canvas), { passive: false });
    canvasElement.addEventListener('touchend', (e) => this.handleTouchEnd(e, canvas), { passive: false });
    canvasElement.addEventListener('touchcancel', (e) => this.handleTouchCancel(e, canvas), { passive: false });

    // Prevent context menu on long press
    canvasElement.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private handleTouchStart(event: TouchEvent, canvas: fabric.Canvas): void {
    event.preventDefault();

    // Store touch information
    Array.from(event.changedTouches).forEach(touch => {
      this.touches.set(touch.identifier, {
        identifier: touch.identifier,
        clientX: touch.clientX,
        clientY: touch.clientY,
        target: touch.target
      });
    });

    // Handle single touch
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      this.handleSingleTouchStart(touch, canvas);
    }

    // Handle multi-touch (pinch gestures)
    else if (event.touches.length === 2) {
      this.handlePinchStart(event.touches, canvas);
    }
  }

  private handleTouchMove(event: TouchEvent, canvas: fabric.Canvas): void {
    event.preventDefault();

    // Clear long press if touch moves significantly
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = undefined;
    }

    if (event.touches.length === 1) {
      this.handleSingleTouchMove(event.touches[0], canvas);
    }
    else if (event.touches.length === 2) {
      this.handlePinchMove(event.touches, canvas);
    }
  }

  private handleTouchEnd(event: TouchEvent, canvas: fabric.Canvas): void {
    event.preventDefault();

    // Clear long press timeout
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = undefined;
    }

    // Remove ended touches from tracking
    Array.from(event.changedTouches).forEach(touch => {
      this.touches.delete(touch.identifier);
    });

    // Handle end of pinch gesture
    if (event.touches.length < 2 && this.isPinching) {
      this.handlePinchEnd(canvas);
    }

    // Handle single touch end
    if (event.touches.length === 0) {
      this.handleSingleTouchEnd(canvas);
    }
  }

  private handleTouchCancel(event: TouchEvent, canvas: fabric.Canvas): void {
    this.handleTouchEnd(event, canvas);
  }

  private handleSingleTouchStart(touch: Touch, canvas: fabric.Canvas): void {
    const pointer = canvas.getPointer({ x: touch.clientX, y: touch.clientY } as any);

    // Simulate mouse down for fabric.js
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY,
      button: 0,
      buttons: 1
    });

    canvas.getElement().dispatchEvent(mouseEvent);

    // Setup long press detection for context menu
    this.longPressTimeout = setTimeout(() => {
      this.handleLongPress(touch, canvas);
    }, 500); // 500ms for long press
  }

  private handleSingleTouchMove(touch: Touch, canvas: fabric.Canvas): void {
    // Simulate mouse move for fabric.js
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY,
      button: 0,
      buttons: 1
    });

    canvas.getElement().dispatchEvent(mouseEvent);
  }

  private handleSingleTouchEnd(canvas: fabric.Canvas): void {
    // Simulate mouse up for fabric.js
    const mouseEvent = new MouseEvent('mouseup', {
      button: 0,
      buttons: 0
    });

    canvas.getElement().dispatchEvent(mouseEvent);

    this.isLongPress = false;
  }

  private handlePinchStart(touches: TouchList, canvas: fabric.Canvas): void {
    if (touches.length !== 2) return;

    this.isPinching = true;
    this.lastPinchDistance = this.getDistance(touches[0], touches[1]);

    // Disable fabric.js selection during pinch
    canvas.selection = false;
    canvas.discardActiveObject();
    canvas.renderAll();
  }

  private handlePinchMove(touches: TouchList, canvas: fabric.Canvas): void {
    if (touches.length !== 2 || !this.isPinching) return;

    const currentDistance = this.getDistance(touches[0], touches[1]);
    const scale = currentDistance / this.lastPinchDistance;

    // Get center point between touches
    const centerX = (touches[0].clientX + touches[1].clientX) / 2;
    const centerY = (touches[0].clientY + touches[1].clientY) / 2;
    const pointer = canvas.getPointer({ x: centerX, y: centerY } as any);

    // Apply zoom with center point
    const currentZoom = canvas.getZoom();
    const newZoom = Math.min(Math.max(currentZoom * scale, 0.1), 5);

    canvas.zoomToPoint(pointer, newZoom);

    this.lastPinchDistance = currentDistance;
  }

  private handlePinchEnd(canvas: fabric.Canvas): void {
    this.isPinching = false;
    this.lastPinchDistance = 0;

    // Re-enable fabric.js selection
    canvas.selection = true;
  }

  private handleLongPress(touch: Touch, canvas: fabric.Canvas): void {
    this.isLongPress = true;

    // Find object at touch position
    const pointer = canvas.getPointer({ x: touch.clientX, y: touch.clientY } as any);
    const target = canvas.findTarget({ x: touch.clientX, y: touch.clientY } as any);

    if (target && (target as any).elementData) {
      // Select object on long press
      canvas.setActiveObject(target);
      canvas.renderAll();

      // Trigger haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }

      // Could emit custom event for context menu
      const contextMenuEvent = new CustomEvent('fabricLongPress', {
        detail: {
          target,
          pointer,
          originalEvent: touch
        }
      });

      canvas.getElement().dispatchEvent(contextMenuEvent);
    }
  }

  private getDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Public method to enable/disable gestures
  enableGestures(canvas: fabric.Canvas, enabled: boolean = true): void {
    if (enabled) {
      this.setupMobileGestures(canvas);
    } else {
      const canvasElement = canvas.getElement();
      canvasElement.style.touchAction = 'auto';
      canvasElement.style.userSelect = 'auto';

      // Remove all touch event listeners
      canvasElement.removeEventListener('touchstart', (e) => this.handleTouchStart(e, canvas));
      canvasElement.removeEventListener('touchmove', (e) => this.handleTouchMove(e, canvas));
      canvasElement.removeEventListener('touchend', (e) => this.handleTouchEnd(e, canvas));
      canvasElement.removeEventListener('touchcancel', (e) => this.handleTouchCancel(e, canvas));
    }
  }

  // Cleanup method
  cleanup(): void {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = undefined;
    }

    this.touches.clear();
    this.isPinching = false;
    this.isLongPress = false;
    this.lastPinchDistance = 0;
  }
}
